// Version 1 — Actual vs Modeled comparison card.
//
// Compares the actual Current Staff Plan headcount against the modeled FTE
// (post profile factors) per (Discipline × Channel). The Hub delta is the
// primary focus — it's amber-tinted, larger, and called out at the card
// header so users can see at a glance how much hub headcount the model
// prescribes vs. what the client has today.
//
// Modeled split comes from the engine's per-cell shore split (`onshoreFte` /
// `nearshoreFte` / `offshoreFte`) on the cells the tab passes in — the modeled
// grid with any manual cell adjustments applied. That is driven by the scenario's
// per-discipline OGS configuration, and matches the FTE-by-Discipline-x-Channel
// table on the same tab so the comparison is apples-to-apples. Actual split
// comes from the StaffPlan's `onFte` / `hubFte` (legacy plans without an
// explicit split read as 100% onshore).

import { useMemo } from 'react';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, Minus, Users } from 'lucide-react';
import { resolveCellSplit, summarizeOtherRoles } from '@rc/engine';
import type {
  DisciplineChannelCell,
  Model,
  StaffPlan,
  StaffPlanCell,
} from '@rc/engine/types';

interface Props {
  model: Model;
  /** The modeled grid with the scenario's manual adjustments applied. */
  cells: DisciplineChannelCell[];
  /** Whether any of those cells were adjusted by hand, so the panel can name
   *  the team it is comparing against. */
  adjusted?: boolean;
  staffPlan: StaffPlan | null | undefined;
}

interface Split {
  on: number;
  hub: number;
  total: number;
}

interface Row {
  disciplineId: string;
  disciplineLabel: string;
  channelId: string;
  channelLabel: string;
  modeled: Split;
  actual: Split;
}

const EPS = 0.05;

function fmt(n: number): string {
  if (!Number.isFinite(n) || Math.abs(n) < EPS) return '·';
  if (Math.abs(n) >= 100) return Math.round(n).toLocaleString();
  return n.toFixed(1);
}

function fmtSigned(n: number): string {
  if (!Number.isFinite(n) || Math.abs(n) < EPS) return '0';
  const v = Math.abs(n) >= 100 ? Math.round(n).toLocaleString() : n.toFixed(1);
  return n > 0 ? `+${v}` : v;
}

/** Roll modeled cells into one Split per (discipline, channel). */
function buildModeledByDC(
  cells: DisciplineChannelCell[],
): Map<string, Split & { disciplineLabel: string; channelLabel: string }> {
  const map = new Map<string, Split & { disciplineLabel: string; channelLabel: string }>();
  for (const c of cells || []) {
    const total = Number(c.adjustedFte) || 0;
    if (Math.abs(total) < EPS) continue;
    const hub = (Number(c.nearshoreFte) || 0) + (Number(c.offshoreFte) || 0);
    const on = Number(c.onshoreFte) || 0;
    const key = `${c.disciplineId}::${c.channelId}`;
    const cur = map.get(key);
    if (cur) {
      cur.on += on;
      cur.hub += hub;
      cur.total += total;
    } else {
      map.set(key, {
        disciplineLabel: c.disciplineLabel,
        channelLabel:
          c.channelId === 'all' ? 'All channels' : c.channelLabel || c.channelId,
        on,
        hub,
        total,
      });
    }
  }
  return map;
}

/** Roll actual staff-plan cells into one Split per (discipline, channel). */
function buildActualByDC(
  cells: StaffPlanCell[] | undefined,
): Map<string, Split> {
  const map = new Map<string, Split>();
  if (!cells || cells.length === 0) return map;
  for (const c of cells) {
    const s = resolveCellSplit(c);
    if (Math.abs(s.total) < EPS) continue;
    const key = `${c.disciplineId}::${c.channelId}`;
    const cur = map.get(key);
    if (cur) {
      cur.on += s.on;
      cur.hub += s.hub;
      cur.total += s.total;
    } else {
      map.set(key, { on: s.on, hub: s.hub, total: s.total });
    }
  }
  return map;
}

function discLabelFor(model: Model, disciplineId: string): string {
  return (model.disciplines || []).find((d) => d.id === disciplineId)?.label || disciplineId;
}
function channelLabelFor(model: Model, channelId: string): string {
  if (channelId === 'all') return 'All channels';
  return (model.channels || []).find((c) => c.id === channelId)?.label || channelId;
}

export default function HubComparisonCard({
  model,
  cells,
  adjusted,
  staffPlan,
}: Props) {
  const modeledWords = adjusted ? 'Modeled-Adjusted' : 'Modeled';
  const channelOrder = useMemo(
    () => new Map((model.channels || []).map((c, i) => [c.id, i])),
    [model.channels],
  );
  const disciplineOrder = useMemo(
    () => new Map((model.disciplines || []).map((d, i) => [d.id, i])),
    [model.disciplines],
  );

  const rows = useMemo<Row[]>(() => {
    const modeled = buildModeledByDC(cells);
    const actual = buildActualByDC(staffPlan?.cells);

    const allKeys = new Set<string>([...modeled.keys(), ...actual.keys()]);
    const rows: Row[] = [];
    for (const key of allKeys) {
      const [disciplineId, channelId] = key.split('::');
      const m = modeled.get(key);
      const a = actual.get(key);
      rows.push({
        disciplineId,
        disciplineLabel: m?.disciplineLabel || discLabelFor(model, disciplineId),
        channelId,
        channelLabel: m?.channelLabel || channelLabelFor(model, channelId),
        modeled: { on: m?.on || 0, hub: m?.hub || 0, total: m?.total || 0 },
        actual: { on: a?.on || 0, hub: a?.hub || 0, total: a?.total || 0 },
      });
    }

    rows.sort((a, b) => {
      const da = disciplineOrder.get(a.disciplineId) ?? Number.POSITIVE_INFINITY;
      const db = disciplineOrder.get(b.disciplineId) ?? Number.POSITIVE_INFINITY;
      if (da !== db) return da - db;
      if (a.channelId === 'all' && b.channelId !== 'all') return -1;
      if (b.channelId === 'all' && a.channelId !== 'all') return 1;
      const ca = channelOrder.get(a.channelId) ?? Number.POSITIVE_INFINITY;
      const cb = channelOrder.get(b.channelId) ?? Number.POSITIVE_INFINITY;
      if (ca !== cb) return ca - cb;
      return a.channelLabel.localeCompare(b.channelLabel);
    });

    return rows;
  }, [model, cells, staffPlan, channelOrder, disciplineOrder]);

  const grand = useMemo(() => {
    const acc = {
      modeled: { on: 0, hub: 0, total: 0 },
      actual: { on: 0, hub: 0, total: 0 },
    };
    for (const r of rows) {
      acc.modeled.on += r.modeled.on;
      acc.modeled.hub += r.modeled.hub;
      acc.modeled.total += r.modeled.total;
      acc.actual.on += r.actual.on;
      acc.actual.hub += r.actual.hub;
      acc.actual.total += r.actual.total;
    }
    return acc;
  }, [rows]);

  const otherRoles = useMemo(() => summarizeOtherRoles(staffPlan), [staffPlan]);

  if (!staffPlan) {
    return (
      <section className="card border-dashed">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-slate-400" />
            <h3 className="text-base font-semibold">
              Actual vs {modeledWords} — Hub delta
            </h3>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Link a current staff plan to this scenario to see the actual headcount
            compared against the modeled split, with the Hub delta highlighted.
          </p>
        </div>
        <div className="card-body text-sm text-slate-500">
          No staff plan linked yet. Open{' '}
          <span className="font-medium text-slate-700">Staff plan</span> from the
          header to create or attach one.
        </div>
      </section>
    );
  }

  const grandHubDelta = grand.actual.hub - grand.modeled.hub;
  const grandTotalDelta = grand.actual.total - grand.modeled.total;

  return (
    <section className="card">
      <div className="card-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Users size={16} className="text-brand-purple-500" />
              <h3 className="text-base font-semibold">
                Actual vs {modeledWords} — Hub delta
              </h3>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Per (Discipline × Channel) comparison of the modeled split (the
              scenario's OGS configuration applied to adjusted FTE
              {adjusted ? ', including your manual cell edits' : ''}) vs the actual
              staff plan{staffPlan.hubLocation ? (
                <>
                  {' '}— hub roles sit at each row's selected hub (default{' '}
                  <span className="font-medium text-slate-700">
                    {staffPlan.hubLocation.label || 'Hub'}
                  </span>{' '}
                  (
                  <span
                    className={
                      staffPlan.hubLocation.shore === 'nearshore'
                        ? 'text-emerald-700 font-medium'
                        : 'text-amber-800 font-medium'
                    }
                  >
                    {staffPlan.hubLocation.shore}
                  </span>
                  ))
                </>
              ) : null}
              .
            </p>
          </div>
          <div className="flex items-center gap-2">
            <DeltaPill
              label="Hub Δ"
              value={grandHubDelta}
              accent="hub"
              size="lg"
              modeled={grand.modeled.hub}
              actual={grand.actual.hub}
              modeledWord={adjusted ? 'adjusted' : 'modeled'}
            />
            <DeltaPill
              label="Total Δ"
              value={grandTotalDelta}
              accent="neutral"
              modeled={grand.modeled.total}
              actual={grand.actual.total}
              modeledWord={adjusted ? 'adjusted' : 'modeled'}
            />
          </div>
        </div>
      </div>
      <div className="card-body overflow-x-auto">
        <table className="w-full text-xs tabular-nums border-separate border-spacing-0">
          <thead>
            <tr className="text-slate-500 uppercase text-[10px] tracking-wider">
              <th rowSpan={2} className="text-left align-bottom py-2 pr-3 font-medium border-b border-slate-200">
                Discipline
              </th>
              <th rowSpan={2} className="text-left align-bottom py-2 pr-3 font-medium border-b border-slate-200">
                Channel
              </th>
              <MetricGroupHeader
                label="Hub"
                tone="hub"
                emphasis
              />
              <MetricGroupHeader label="Onshore" tone="on" />
              <MetricGroupHeader label="Total" tone="neutral" />
            </tr>
            <tr className="text-[9px] tracking-wider text-slate-400">
              <SubHeaders tone="hub" modeledLabel={modeledWords} />
              <SubHeaders tone="on" modeledLabel={modeledWords} />
              <SubHeaders tone="neutral" modeledLabel={modeledWords} />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={11} className="py-6 text-center text-slate-400 text-sm">
                  No discipline × channel data available for this scenario yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const hubΔ = r.actual.hub - r.modeled.hub;
                const onΔ = r.actual.on - r.modeled.on;
                const totalΔ = r.actual.total - r.modeled.total;
                return (
                  <tr
                    key={`${r.disciplineId}::${r.channelId}`}
                    className="border-t border-slate-100 hover:bg-slate-50/40"
                  >
                    <td className="py-1.5 pr-3 text-slate-700 font-medium whitespace-nowrap border-t border-slate-100">
                      {r.disciplineLabel}
                    </td>
                    <td className="py-1.5 pr-3 text-slate-600 whitespace-nowrap border-t border-slate-100">
                      {r.channelLabel}
                    </td>
                    <MetricCells
                      modeled={r.modeled.hub}
                      actual={r.actual.hub}
                      delta={hubΔ}
                      tone="hub"
                      emphasis
                    />
                    <MetricCells
                      modeled={r.modeled.on}
                      actual={r.actual.on}
                      delta={onΔ}
                      tone="on"
                    />
                    <MetricCells
                      modeled={r.modeled.total}
                      actual={r.actual.total}
                      delta={totalΔ}
                      tone="neutral"
                    />
                  </tr>
                );
              })
            )}
            <tr className="bg-brand-purple-50/40">
              <td colSpan={2} className="py-2 pr-3 font-semibold text-brand-purple-800 border-t-2 border-slate-300">
                Total
              </td>
              <MetricCells
                modeled={grand.modeled.hub}
                actual={grand.actual.hub}
                delta={grandHubDelta}
                tone="hub"
                emphasis
                strong
              />
              <MetricCells
                modeled={grand.modeled.on}
                actual={grand.actual.on}
                delta={grand.actual.on - grand.modeled.on}
                tone="on"
                strong
              />
              <MetricCells
                modeled={grand.modeled.total}
                actual={grand.actual.total}
                delta={grandTotalDelta}
                tone="neutral"
                strong
              />
            </tr>
            {otherRoles.length > 0 && (
              <>
                <tr className="bg-slate-50/60">
                  <td colSpan={11} className="pt-4 pb-1.5 pr-3 border-t border-slate-200">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                      Other (not modeled)
                    </span>
                    <span className="ml-2 text-[11px] text-slate-500">
                      Roles without a model discipline — actual only, excluded from the
                      comparison.
                    </span>
                  </td>
                </tr>
                {otherRoles.map((o) => (
                  <tr key={o.role}>
                    <td className="py-1.5 pr-3 text-slate-700 font-medium whitespace-nowrap border-t border-slate-100">
                      {o.role}
                    </td>
                    <td className="py-1.5 pr-3 text-slate-400 border-t border-slate-100">—</td>
                    <ActualOnlyCells actual={o.hub} tone="hub" />
                    <ActualOnlyCells actual={o.on} tone="on" />
                    <ActualOnlyCells actual={o.total} tone="neutral" />
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] uppercase tracking-wider text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm border border-amber-200 bg-amber-50/60" />
            Hub — offshore + nearshore
          </span>
          <span className="inline-flex items-center gap-1.5 text-emerald-700">
            <ArrowUp size={11} /> Actual exceeds modeled
          </span>
          <span className="inline-flex items-center gap-1.5 text-rose-600">
            <ArrowDown size={11} /> Actual under modeled
          </span>
        </div>
      </div>
    </section>
  );
}

function MetricGroupHeader({
  label,
  tone,
  emphasis,
}: {
  label: string;
  tone: 'hub' | 'on' | 'neutral';
  emphasis?: boolean;
}) {
  const bg =
    tone === 'hub'
      ? 'bg-amber-50/60 text-amber-800'
      : tone === 'on'
        ? 'text-slate-600'
        : 'text-slate-600';
  return (
    <th
      colSpan={3}
      className={clsx(
        'text-center pt-2 pb-1 px-1.5 font-semibold border-b border-slate-100',
        bg,
        emphasis && 'tracking-wider',
      )}
    >
      {label}
    </th>
  );
}

function SubHeaders({
  tone,
  modeledLabel,
}: {
  tone: 'hub' | 'on' | 'neutral';
  modeledLabel: string;
}) {
  const baseHubBg = tone === 'hub' ? 'bg-amber-50/40 text-amber-700/70' : '';
  return (
    <>
      <th
        className={clsx('font-medium text-right pb-2 pl-2 pr-1 border-b border-slate-200 border-l border-slate-100', baseHubBg)}
        title={modeledLabel}
      >
        {/* The column is narrow, so an adjusted grid shortens to "Adjusted"
            rather than wrapping the full label across two lines. */}
        {modeledLabel === 'Modeled' ? 'Modeled' : 'Adjusted'}
      </th>
      <th className={clsx('font-medium text-right pb-2 px-1 border-b border-slate-200', baseHubBg)}>
        Actual
      </th>
      <th className={clsx('font-medium text-right pb-2 pl-1 pr-2 border-b border-slate-200', baseHubBg)}>
        Δ
      </th>
    </>
  );
}

function MetricCells({
  modeled,
  actual,
  delta,
  tone,
  emphasis,
  strong,
}: {
  modeled: number;
  actual: number;
  delta: number;
  tone: 'hub' | 'on' | 'neutral';
  emphasis?: boolean;
  strong?: boolean;
}) {
  const wrapperBg = tone === 'hub' ? 'bg-amber-50/40' : '';
  const baseText = tone === 'hub' ? 'text-amber-900/80' : 'text-slate-700';
  const strongClass = strong ? 'font-semibold' : '';
  return (
    <>
      <td
        className={clsx(
          'py-1.5 pl-2 pr-1 text-right border-l border-slate-100 border-t border-slate-100',
          wrapperBg,
          baseText,
          strongClass,
        )}
      >
        {fmt(modeled)}
      </td>
      <td
        className={clsx(
          'py-1.5 px-1 text-right border-t border-slate-100',
          wrapperBg,
          baseText,
          strongClass,
        )}
      >
        {fmt(actual)}
      </td>
      <td
        className={clsx(
          'py-1.5 pl-1 pr-2 text-right border-t border-slate-100',
          wrapperBg,
        )}
      >
        <DeltaInline value={delta} emphasis={emphasis} strong={strong} />
      </td>
    </>
  );
}

function ActualOnlyCells({ actual, tone }: { actual: number; tone: 'hub' | 'on' | 'neutral' }) {
  const wrapperBg = tone === 'hub' ? 'bg-amber-50/40' : '';
  const baseText = tone === 'hub' ? 'text-amber-900/80' : 'text-slate-700';
  return (
    <>
      <td className={clsx('py-1.5 pl-2 pr-1 text-right text-slate-300 border-l border-slate-100 border-t border-slate-100', wrapperBg)}>
        —
      </td>
      <td className={clsx('py-1.5 px-1 text-right border-t border-slate-100', wrapperBg, baseText)}>
        {fmt(actual)}
      </td>
      <td className={clsx('py-1.5 pl-1 pr-2 text-right text-slate-300 border-t border-slate-100', wrapperBg)}>
        —
      </td>
    </>
  );
}

function DeltaInline({
  value,
  emphasis,
  strong,
}: {
  value: number;
  emphasis?: boolean;
  strong?: boolean;
}) {
  if (!Number.isFinite(value) || Math.abs(value) < EPS) {
    return (
      <span className={clsx('inline-flex items-center gap-0.5 text-slate-400', strong && 'font-semibold')}>
        <Minus size={emphasis ? 12 : 10} className="opacity-60" /> 0
      </span>
    );
  }
  const positive = value > 0;
  const color = positive ? 'text-emerald-700' : 'text-rose-600';
  const Icon = positive ? ArrowUp : ArrowDown;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-0.5',
        color,
        strong && 'font-semibold',
        emphasis && 'tabular-nums',
      )}
    >
      <Icon size={emphasis ? 12 : 10} />
      {fmtSigned(value)}
    </span>
  );
}

function DeltaPill({
  label,
  value,
  accent,
  size,
  modeled,
  actual,
  modeledWord,
}: {
  label: string;
  value: number;
  accent: 'hub' | 'neutral';
  size?: 'lg';
  modeled: number;
  actual: number;
  modeledWord: string;
}) {
  const positive = value > 0;
  const negligible = Math.abs(value) < EPS;
  const tone = negligible
    ? 'border-slate-200 bg-white text-slate-500'
    : accent === 'hub'
      ? positive
        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
        : 'border-rose-200 bg-rose-50 text-rose-800'
      : positive
        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
        : 'border-rose-200 bg-rose-50 text-rose-800';

  return (
    <div
      className={clsx(
        'rounded-lg border px-3 py-1.5 flex flex-col items-end',
        tone,
        size === 'lg' ? 'min-w-[150px]' : 'min-w-[120px]',
      )}
    >
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider opacity-70">
        {accent === 'hub' && (
          <span className="inline-block w-2 h-2 rounded-sm bg-amber-400/60 border border-amber-500/60" />
        )}
        {label}
      </div>
      <div
        className={clsx(
          'tabular-nums font-semibold',
          size === 'lg' ? 'text-xl' : 'text-base',
        )}
      >
        {negligible ? '0' : fmtSigned(value)}
      </div>
      <div className="text-[10px] text-current opacity-70 tabular-nums">
        actual {fmt(actual)} · {modeledWord} {fmt(modeled)}
      </div>
    </div>
  );
}
